import type { ReactNode } from "react";

export interface SwitchProps {
  /** つまみの状態 */
  checked: boolean;
  /** 切り替わったときに呼ばれる */
  onChange: (next: boolean) => void;
  /** つまみの右に出す内容 (ラベルやアイコン) */
  children?: ReactNode;
  /** 追加のクラス名。箱の見た目 (枠・地・余白) は使う側が決める */
  className?: string;
  /** 読み上げ名。children に字が無いときは必須 */
  "aria-label"?: string;
  disabled?: boolean;
  /** 説明や誤りの要素の id。フォームの部品が名前と結ぶ */
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  "aria-required"?: boolean;
  /** E2E/contract test用の安定した識別子 */
  testId?: string;
}

/*
on/off のつまみ。role="switch" で aria-checked を持つ箱に、
丸が左右へ動く track を添える。つまみの脇に出すもの (ラベル、
アイコン、補足) は children で渡すので、1つの釦の中に状態と名前を
並べられる

track の色はデザイントークンで書く。bg-primary は暗い面でも
ブランドの主色として読める。丸は常に白 — track の上に乗る
「つまみ」は両テーマで同じ白い部品なので固定色にする
*/
export function Switch({
  checked,
  onChange,
  children,
  className = "",
  "aria-label": ariaLabel,
  disabled = false,
  testId,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
  "aria-required": required,
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-describedby={describedBy}
      aria-invalid={invalid}
      aria-required={required}
      disabled={disabled}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center gap-2.5 transition-colors focus-visible:shadow-[0_0_0_2px_var(--color-bg-main),0_0_0_4px_var(--color-bg-primary)] focus-visible:outline-none disabled:opacity-50 ${className}`}
    >
      <Track on={checked} />
      {children}
    </button>
  );
}

/*
つまみは span で描く。中の丸を translate で動かすので、track 側は
relative のまま高さを固定する。**scale 系のユーティリティを使わない**
のは、Tailwind v4 だと scale プロパティに落ちて transform と
喧嘩するため
*/
function Track({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
        on ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
          on ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </span>
  );
}
